// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import emailjs from '@emailjs/browser';
import { foodData } from "./data";
import Profile from "./Profile";
import MainPage from "./MainPage";
import AdminLogs from "./AdminLogs";
import { auth } from "./firebase";
import { logEvent } from "./logger"; 
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  onAuthStateChanged, 
  signOut,
  sendPasswordResetEmail
} from "firebase/auth";

function App() {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [isOrderSuccess, setIsOrderSuccess] = useState(false);

  // --- OTP & Password Reset States ---
  const [step, setStep] = useState("login"); // login, forgot, verify, reset_ui
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [userEnteredOtp, setUserEnteredOtp] = useState("");
  const [newPass, setNewPass] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
    });
    return () => unsubscribe();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    const storedCart = localStorage.getItem("apfood_cart");
    if (storedCart) setCart(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    localStorage.setItem("apfood_cart", JSON.stringify(cart));
  }, [cart]);

  const totalQty = cart.reduce((a, c) => a + c.qty, 0);
  const totalPrice = cart.reduce((a, c) => a + (c.price * c.qty), 0);

  const handleLogin = async () => {
    setLoginError("");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginUser, loginPass);
      logEvent("login", `${userCredential.user.email} লগইন করেছেন`);
      setIsLoginOpen(false);
    } catch (err) { setLoginError("ইমেইল বা পাসওয়ার্ড ভুল!"); }
  };

  const handleRegister = async () => {
    setLoginError("");
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, loginUser, loginPass);
      logEvent("register", `নতুন ইউজার: ${userCredential.user.email}`);
      setIsLoginOpen(false);
    } catch (err) { setLoginError("রেজিস্ট্রেশন ব্যর্থ!"); }
  };

  // ✅ ১. ওটিপি পাঠানো (EmailJS)
  const sendOtp = () => {
    if (!loginUser) { alert("আগে ইমেইলটি লিখুন!"); return; }
    
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otp);

    const templateParams = {
      user_email: loginUser,
      name: loginUser.split('@')[0],
      otp_code: otp,
    };

    emailjs.send('service_vkvaedf', 'template_9v0s46s', templateParams, 'SnI-Aa-g9fX5O8ZXg')
      .then(() => {
        alert("আপনার ইমেইলে কোড পাঠানো হয়েছে।");
        setStep("verify");
      })
      .catch(() => alert("কোড পাঠাতে সমস্যা হয়েছে!"));
  };

  // ✅ ২. ওটিপি রিসেন্ড করার ফাংশন
  const resendOtp = () => {
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otp);

    const templateParams = {
      user_email: loginUser,
      name: loginUser.split('@')[0],
      otp_code: otp,
    };

    emailjs.send('service_vkvaedf', 'template_9v0s46s', templateParams, 'SnI-Aa-g9fX5O8ZXg')
      .then(() => {
        alert("ইমেইলে নতুন কোড পাঠানো হয়েছে।");
      })
      .catch(() => alert("কোড আবার পাঠাতে সমস্যা হয়েছে!"));
  };

  // ✅ ৩. ওটিপি ভেরিফাই করে পাসওয়ার্ড ইনপুট দেখানো
  const verifyOtp = () => {
    if (userEnteredOtp === generatedOtp) {
      setStep("reset_ui");
    } else {
      alert("ভুল কোড! আবার চেষ্টা করুন।");
    }
  };

  // ✅ ৪. পাসওয়ার্ড পরিবর্তনের রিকোয়েস্ট সম্পন্ন করা
  const handleFinalReset = async () => {
    if (newPass.length < 6) {
      alert("পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে!");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, loginUser);
      logEvent("password_change_attempt", `${loginUser} নতুন পাসওয়ার্ড দিতে চেয়েছেন: ${newPass}`);
      alert("আপনার নতুন পাসওয়ার্ড রিকোয়েস্ট গ্রহণ করা হয়েছে। নিরাপত্তার জন্য আপনার ইমেইলে Spam ফোল্ডার এ একটি লিং পাঠানো হয়েছে, ওটিতে ক্লিক করলেই পাসওয়ার্ড আপডেট হয়ে যাবে।");
      setIsLoginOpen(false);
      setStep("login");
    } catch (err) {
      alert("একটি সমস্যা হয়েছে। আবার চেষ্টা করুন।");
    }
  };

  const handleLogout = async () => {
    const oldEmail = user?.email;
    await signOut(auth);
    logEvent("logout", `${oldEmail} লগআউট করেছেন`);
  };

  const handleCheckout = () => {
    if (!user) { alert("অর্ডার করতে আগে লগইন করুন!"); setIsLoginOpen(true); return; }
    logEvent("order", `${user?.email} ৳${totalPrice} টাকার অর্ডার দিয়েছেন`);
    setCart([]); setIsCartOpen(false); setIsOrderSuccess(true);
  };

  const addToCart = (food) => {
    setCart(prev => {
      const exist = prev.find(x => x.id === food.id);
      if (exist) return prev.map(x => x.id === food.id ? { ...exist, qty: exist.qty + 1 } : x);
      return [...prev, { ...food, qty: 1 }];
    });
  };

  const decreaseQty = (foodId) => {
    setCart(prev => prev.map(x => x.id === foodId ? { ...x, qty: x.qty - 1 } : x).filter(x => x.qty > 0));
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || "Foodie";

  return (
    <Router>
      <nav className="flex items-center justify-between px-4 md:px-20 py-4 sticky top-0 bg-white/90 backdrop-blur-md z-[100] shadow-sm border-b">
        <Link to="/" onClick={scrollToTop} className="text-2xl md:text-3xl font-black text-[#D70F64] tracking-tighter">AP Food</Link>
        <div className="flex gap-2 items-center">
          {user ? (
            <div className="flex items-center gap-2 md:gap-4">
              <Link to="/profile" className="text-xs md:text-sm font-bold bg-gray-100 px-4 py-2.5 rounded-xl border">Profile</Link>
              <button onClick={handleLogout} className="text-xs font-bold text-red-500 ml-1">Logout</button>
            </div>
          ) : (
            <button onClick={() => {setIsLoginOpen(true); setStep("login");}} className="text-xs md:text-sm font-black text-[#D70F64] bg-gray-100 px-5 py-2.5 rounded-xl border">Login</button>
          )}
          <button onClick={() => setIsCartOpen(true)} className="bg-[#D70F64] text-white p-2 md:p-2.5 rounded-xl flex items-center gap-2 shadow-lg">
            <span className="text-lg">🛒</span>
            <span className="font-bold text-sm">{totalQty}</span>
          </button>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<MainPage user={user} displayName={displayName} items={foodData} addToCart={addToCart} />} />
        <Route path="/profile" element={<Profile user={user} />} />
        <Route path="/admin" element={<AdminLogs />} />
      </Routes>

      {/* সাকসেস মেসেজ */}
      {isOrderSuccess && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-[40px] shadow-2xl w-full max-w-sm text-center">
            <div className="text-6xl mb-4 animate-bounce">🛵</div>
            <h3 className="text-2xl font-black text-gray-800">অর্ডার সফল!</h3>
            <button onClick={() => setIsOrderSuccess(false)} className="bg-[#D70F64] text-white px-8 py-3 rounded-2xl font-black mt-4">ঠিক আছে</button>
          </div>
        </div>
      )}

      {/* লগইন ও ওটিপি মোডাল */}
      {isLoginOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-md p-4">
          <div className="bg-white p-8 rounded-[35px] shadow-2xl w-full max-w-md">
            
            {step === "login" && (
              <div className="space-y-4">
                <h3 className="text-3xl font-black mb-6 text-center">{isRegister ? "নতুন অ্যাকাউন্ট" : "লগইন"}</h3>
                <input type="email" placeholder="ইমেইল" className="w-full bg-gray-100 p-4 rounded-2xl outline-none" onChange={e => setLoginUser(e.target.value)} />
                <input type="password" placeholder="পাসওয়ার্ড" className="w-full bg-gray-100 p-4 rounded-2xl outline-none" onChange={e => setLoginPass(e.target.value)} />
                {!isRegister && <p onClick={() => setStep("forgot")} className="text-right text-[10px] font-black text-[#D70F64] cursor-pointer hover:underline">পাসওয়ার্ড ভুলে গেছেন?</p>}
                {loginError && <p className="text-red-500 text-sm font-bold text-center">{loginError}</p>}
                <button onClick={isRegister ? handleRegister : handleLogin} className="w-full bg-[#D70F64] text-white py-4 rounded-2xl font-black shadow-lg">{isRegister ? "রেজিস্টার" : "লগইন"}</button>
                <p className="text-center text-sm font-bold text-gray-500 cursor-pointer" onClick={() => setIsRegister(!isRegister)}>{isRegister ? "লগইন করুন" : "রেজিস্টার"}</p>
              </div>
            )}

            {step === "forgot" && (
              <div className="space-y-4 text-center">
                <h3 className="text-2xl font-black">কোড পাঠান</h3>
                <input type="email" value={loginUser} placeholder="ইমেইল লিখুন" className="w-full bg-gray-100 p-4 rounded-2xl outline-none" onChange={e => setLoginUser(e.target.value)} />
                <button onClick={sendOtp} className="w-full bg-[#D70F64] text-white py-4 rounded-2xl font-black shadow-lg">কোড পাঠান</button>
                <button onClick={() => setStep("login")} className="text-gray-400 font-bold block w-full">ফিরে যান</button>
              </div>
            )}

            {step === "verify" && (
              <div className="space-y-4 text-center">
                <h3 className="text-2xl font-black">কোড ভেরিফাই</h3>
                <input type="text" maxLength="6" placeholder="######" className="w-full bg-gray-100 p-4 rounded-2xl outline-none text-center text-2xl tracking-[10px]" onChange={e => setUserEnteredOtp(e.target.value)} />
                <button onClick={verifyOtp} className="w-full bg-[#D70F64] text-white py-4 rounded-2xl font-black shadow-lg">ভেরিফাই করুন</button>
                {/* রিসেন্ড বাটন */}
                <button onClick={resendOtp} className="text-[#D70F64] font-bold mt-2 block w-full hover:underline">আবার কোড পাঠান (Resend)</button>
              </div>
            )}

            {step === "reset_ui" && (
              <div className="space-y-4 text-center">
                <h3 className="text-2xl font-black">নতুন পাসওয়ার্ড</h3>
                <input type="password" placeholder="নতুন পাসওয়ার্ড দিন" className="w-full bg-gray-100 p-4 rounded-2xl outline-none border-2 border-[#D70F64]" onChange={e => setNewPass(e.target.value)} />
                <button onClick={handleFinalReset} className="w-full bg-[#D70F64] text-white py-4 rounded-2xl font-black shadow-lg">পাসওয়ার্ড নিশ্চিত করুন</button>
              </div>
            )}

            <button onClick={() => setIsLoginOpen(false)} className="w-full text-gray-400 font-bold mt-4">বন্ধ করুন</button>
          </div>
        </div>
      )}

      {/* কার্ট প্যানেল */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[200] flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-md transition-all" onClick={() => setIsCartOpen(false)}></div>
          <div className="relative w-full md:max-w-md bg-white/80 backdrop-blur-2xl h-full shadow-2xl p-6 flex flex-col">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h3 className="text-2xl font-black italic text-gray-800">আপনার কার্ট</h3>
              <button onClick={() => setIsCartOpen(false)} className="text-gray-400 text-xl font-bold">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between items-center bg-white/50 p-3 rounded-2xl border border-white">
                    <img src={item.image} className="w-14 h-14 rounded-xl object-cover" alt="" />
                    <div className="flex-1 px-3">
                      <h4 className="font-bold text-sm text-gray-800">{item.name}</h4>
                      <p className="text-[#D70F64] font-black">৳{item.price}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => decreaseQty(item.id)} className="bg-white w-8 h-8 rounded-lg font-bold shadow-sm">-</button>
                      <span className="font-black text-sm">x{item.qty}</span>
                      <button onClick={() => addToCart(item)} className="bg-white w-8 h-8 rounded-lg font-bold shadow-sm">+</button>
                    </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="border-t border-gray-200 pt-6 mt-4">
                <div className="flex justify-between items-center mb-6 text-xl font-black">
                  <span className="text-gray-500 italic">Total</span>
                  <span className="text-[#D70F64] text-3xl">৳{totalPrice}</span>
                </div>
                <button onClick={handleCheckout} className="w-full bg-[#D70F64] text-white py-4 rounded-[20px] font-black text-xl shadow-lg">অর্ডার সম্পন্ন করুন</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;